import { useRef, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { track } from '../lib/analytics';

// Dev placeholder track (CC BY-NC-SA) — see public/audio/NOTICE.md; must be
// replaced or commercially licensed before production.
const SAMPLE_SRC = '/audio/be-brave-antony-raijekov.mp3';

/** Hero "Play example music" control — toggles playback of the sample track. */
export function AudioPreviewButton() {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    el
      .play()
      .then(() => {
        setPlaying(true);
        track('play_sample', { section: 'hero' });
      })
      .catch(() => {
        // Playback can be rejected (autoplay policy, missing file) — stay paused.
        setPlaying(false);
      });
  };

  return (
    <Flex mt="44px" align="center" gap="14px">
      <audio ref={audioRef} src={SAMPLE_SRC} preload="none" onEnded={() => setPlaying(false)} />
      <Flex
        as="button"
        type="button"
        onClick={toggle}
        w="40px"
        h="40px"
        borderRadius="full"
        bg="transparent"
        border="2px solid"
        borderColor="onboarding.mutedRose"
        align="center"
        justify="center"
        flexShrink={0}
        aria-pressed={playing}
        aria-label={playing ? t('landing.a11y.stopSample') : t('landing.a11y.playSample')}
        _hover={{ borderColor: 'onboarding.warmLight' }}
      >
        {playing ? (
          <Flex gap="3px" align="center" aria-hidden>
            <Box w="3px" h="12px" bg="onboarding.warmLight" borderRadius="1px" />
            <Box w="3px" h="12px" bg="onboarding.warmLight" borderRadius="1px" />
          </Flex>
        ) : (
          <Box
            w={0}
            h={0}
            borderTop="6px solid transparent"
            borderBottom="6px solid transparent"
            borderLeft="10px solid"
            borderLeftColor="onboarding.warmLight"
            ml="2px"
            aria-hidden
          />
        )}
      </Flex>
      <Text fontSize="md" fontWeight={600} color="white">
        {t('landing.hero.playExample')}
      </Text>
    </Flex>
  );
}
